import { apiFetch } from "./client";

/**
 * Transport Manager API client. Mirrors `TransportDtos.java`
 * field-for-field — trip DTOs plus the carrier/vehicle/driver codebook
 * reads and the create/update/deactivate writes of the
 * carriers admin screen.
 */

export type TripDirection = "INBOUND" | "OUTBOUND";
export type TripStatus = "PLANNED" | "ARRIVED" | "DEPARTED" | "CANCELLED";
export type VehicleKind = "TRUCK" | "TRAILER";
export type WeighingKind = "GROSS" | "TARE" | "CONTROL";
/** MANUAL when a person typed it, SCALE when the weighbridge sent it directly
 * — this app only ever produces MANUAL readings (see `RecordWeighingRequest`
 * on the backend), but the field is read here regardless of source. */
export type WeighingSource = "MANUAL" | "SCALE";

export type TripItemView = {
  id: string;
  position: number;
  deliveryId: string | null;
  orderId: string | null;
  /** Nullable in the database — an item may be added before anyone knows the mass.
   * Sum with care: the column is nullable even though most rows carry a value. */
  quantityKg: number | null;
  note: string | null;
  /**
   * Whether this caller may know whose load this is.
   *
   * Orders are narrowed per sales rep and deliveries per assigned otkup company,
   * so a trip can legitimately carry a counterparty the viewer is not entitled to
   * see. Such an item is still listed, still with its mass — hiding it would make
   * the lorry's load total lie — but every identifying field below is null.
   *
   * `inScope: true` with a null name means something else: the document itself is
   * gone. Do not collapse the two.
   */
  inScope: boolean;
  counterpartyCompanyId: string | null;
  counterpartyName: string | null;
  /** Order number, outbound only — a delivery announcement carries no number. */
  documentRef: string | null;
  /** Announced date, inbound only: what identifies a delivery instead of a number. */
  documentDate: string | null;
  city: string | null;
};

export type TripView = {
  id: string;
  tripNumber: string;
  direction: TripDirection;
  status: TripStatus;
  carrierId: string | null;
  vehicleId: string | null;
  trailerId: string | null;
  driverId: string | null;
  plannedAt: string | null;
  gateInAt: string | null;
  gateOutAt: string | null;
  note: string | null;
  items: TripItemView[];
  updatedAt: string;
};

export type CreateTripRequest = {
  direction: TripDirection;
  carrierId?: string;
  vehicleId?: string;
  trailerId?: string;
  driverId?: string;
  plannedAt?: string;
  note?: string;
};

/** Exactly one of `deliveryId`/`orderId` must match the trip's own direction — enforced server-side. */
export type AddTripItemRequest = {
  deliveryId?: string;
  orderId?: string;
  quantityKg?: number;
  note?: string;
};

export type CarrierView = {
  id: string;
  name: string;
  taxId: string | null;
  phone: string | null;
  email: string | null;
  active: boolean;
  updatedAt: string;
};

export type VehicleView = {
  id: string;
  carrierId: string;
  plate: string;
  kind: VehicleKind;
  capacityKg: number | null;
  active: boolean;
  updatedAt: string;
};

export type DriverView = {
  id: string;
  carrierId: string;
  fullName: string;
  phone: string | null;
  active: boolean;
  updatedAt: string;
};

/** Mirrors `TransportDtos.CreateCarrierRequest` exactly. */
export type CreateCarrierRequest = {
  name: string;
  taxId?: string;
  phone?: string;
  email?: string;
};

/** Mirrors `TransportDtos.UpdateCarrierRequest` exactly — every field optional,
 * a `PUT` only changes what it sends. */
export type UpdateCarrierRequest = {
  name?: string;
  taxId?: string;
  phone?: string;
  email?: string;
  active?: boolean;
};

/** Mirrors `TransportDtos.CreateVehicleRequest` exactly. */
export type CreateVehicleRequest = {
  plate: string;
  kind: VehicleKind;
  capacityKg?: number;
};

/** Mirrors `TransportDtos.UpdateVehicleRequest` exactly. */
export type UpdateVehicleRequest = {
  plate?: string;
  kind?: VehicleKind;
  capacityKg?: number;
  active?: boolean;
};

/** Mirrors `TransportDtos.CreateDriverRequest` exactly. */
export type CreateDriverRequest = {
  fullName: string;
  phone?: string;
};

/** Mirrors `TransportDtos.UpdateDriverRequest` exactly. */
export type UpdateDriverRequest = {
  fullName?: string;
  phone?: string;
  active?: boolean;
};

/** Mirrors `TransportDtos.WeighingView` exactly — one weighbridge reading. */
export type WeighingView = {
  id: string;
  tripId: string;
  kind: WeighingKind;
  scale: string | null;
  weightKg: number;
  measuredAt: string;
  ticketNo: string | null;
  source: WeighingSource;
  /** Only ever set for CONTROL readings — a normal GROSS/TARE reading needs no reason. */
  reason: string | null;
};

/**
 * Mirrors `TransportDtos.GateTripView` exactly — a trip as the gate
 * sees it: enough to identify the truck and where it stands, without the
 * item list (items are `TripService`'s concern, not the gate's — see the
 * backend record's own javadoc).
 *
 * Carries resolved `carrierName`/`vehiclePlate`/
 * `trailerPlate`/`driverName` rather than raw ids: `GatePage` never calls the
 * carrier codebook itself (`PORTIR` holds neither `trips:view` nor
 * `carriers:manage`), and an operator needs a registration plate, not a
 * truncated UUID, to match the truck in front of them to a row on screen.
 * `GateService.today` resolves these names server-side, batched once per
 * call, before the response ever leaves the backend. Any of the four may be `null` (no trailer/driver on the
 * trip) — render that the same way the rest of the app renders an absent
 * value, not as the literal string "null".
 */
export type GateTripView = {
  id: string;
  tripNumber: string;
  direction: TripDirection;
  status: TripStatus;
  carrierName: string | null;
  vehiclePlate: string | null;
  trailerPlate: string | null;
  driverName: string | null;
  plannedAt: string | null;
  gateInAt: string | null;
  gateOutAt: string | null;
};

/** Mirrors `TransportDtos.RecordWeighingRequest` exactly. No `source` field —
 * the server always stamps `MANUAL` for a request coming through this API;
 * `SCALE` is reserved for a future direct weighbridge integration. */
export type RecordWeighingRequest = {
  kind: WeighingKind;
  scale?: string;
  weightKg: number;
  ticketNo?: string;
  reason?: string;
};

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export const tripsApi = {
  /**
   * `deliveryId`/`orderId` answer "which trip is
   * this document on" as a single targeted backend lookup — mutually
   * exclusive with each other and with the direction/date/status filters,
   * which the server ignores once either is set. Added after the original
   * cut called this with no params at all from `DeliveryDetailPage`/
   * `OrderEditorPage` to answer the same question, which meant every trip
   * ever planned (plus one items query per trip) on every single delivery or
   * order view — see `TripService#list`'s javadoc for the full story.
   */
  list: (
    params: {
      direction?: TripDirection;
      from?: string;
      to?: string;
      status?: TripStatus;
      deliveryId?: string;
      orderId?: string;
    } = {},
  ) => apiFetch<TripView[]>(`/transport/trips${q(params)}`),
  get: (id: string) => apiFetch<TripView>(`/transport/trips/${id}`),
  create: (body: CreateTripRequest) => apiFetch<TripView>("/transport/trips", { method: "POST", body }),
  addItem: (id: string, body: AddTripItemRequest) =>
    apiFetch<TripView>(`/transport/trips/${id}/items`, { method: "POST", body }),
  removeItem: (id: string, itemId: string) =>
    apiFetch<void>(`/transport/trips/${id}/items/${itemId}`, { method: "DELETE" }),
  /** Inbound only — outbound quantities come from the orders and our scale merely
   * checks them. The server refuses an outbound trip with ALLOCATION_OUTBOUND. */
  allocation: (id: string) =>
    apiFetch<TripAllocationView>(`/transport/trips/${id}/allocation`),
  allocate: (id: string, body: AllocateTripRequest) =>
    apiFetch<TripAllocationView>(`/transport/trips/${id}/allocation`, { method: "POST", body }),
  /** Reason is optional, matching the controller's `@RequestBody(required = false)`. */
  cancel: (id: string, reason?: string) =>
    apiFetch<TripView>(`/transport/trips/${id}/cancel`, {
      method: "POST",
      body: reason ? { reason } : undefined,
    }),
};

/**
 * Every GET here is open to `trips:view` too, since anyone allowed to see a
 * trip needs the names behind it (carrier/vehicle labels on the board). The
 * writes (create/update/deactivate — the carriers admin screen) need
 * `carriers:manage`; the backend answers 409 `CARRIER_EXISTS` /
 * `VEHICLE_PLATE_EXISTS` for a duplicate name/plate, case-insensitively — the
 * caller surfaces those by `ApiError.code`, not by parsing `message`.
 * Deactivation is a soft delete on the backend (`deletedAt` set), which also
 * drops the row out of every list call above — there is no "reactivate".
 */
export const carriersApi = {
  list: () => apiFetch<CarrierView[]>("/transport/carriers"),
  listVehicles: () => apiFetch<VehicleView[]>("/transport/carriers/vehicles"),
  listDrivers: () => apiFetch<DriverView[]>("/transport/carriers/drivers"),
  create: (body: CreateCarrierRequest) => apiFetch<CarrierView>("/transport/carriers", { method: "POST", body }),
  update: (id: string, body: UpdateCarrierRequest) =>
    apiFetch<CarrierView>(`/transport/carriers/${id}`, { method: "PUT", body }),
  deactivate: (id: string) => apiFetch<void>(`/transport/carriers/${id}`, { method: "DELETE" }),
  createVehicle: (carrierId: string, body: CreateVehicleRequest) =>
    apiFetch<VehicleView>(`/transport/carriers/${carrierId}/vehicles`, { method: "POST", body }),
  updateVehicle: (id: string, body: UpdateVehicleRequest) =>
    apiFetch<VehicleView>(`/transport/carriers/vehicles/${id}`, { method: "PUT", body }),
  deactivateVehicle: (id: string) => apiFetch<void>(`/transport/carriers/vehicles/${id}`, { method: "DELETE" }),
  createDriver: (carrierId: string, body: CreateDriverRequest) =>
    apiFetch<DriverView>(`/transport/carriers/${carrierId}/drivers`, { method: "POST", body }),
  updateDriver: (id: string, body: UpdateDriverRequest) =>
    apiFetch<DriverView>(`/transport/carriers/drivers/${id}`, { method: "PUT", body }),
  deactivateDriver: (id: string) => apiFetch<void>(`/transport/carriers/drivers/${id}`, { method: "DELETE" }),
};

/**
 * The weighbridge gate. `today`/`arrive`/`depart`/`weigh`
 * are `trips:gate`-only on the backend — `PORTIR` holds that permission and
 * nothing else in this module, not even `trips:view`. `weighings` is the one
 * exception: `GateController` also accepts `trips:view` on that route (a
 * sanctioned exception), so the trip detail page reads
 * it directly too.
 */
export const gateApi = {
  /** Today's expected trips (PLANNED/ARRIVED only) — scoped server-side, the
   * gate's entire world. Never call `tripsApi.list` from the gate screen:
   * `PORTIR` lacks `trips:view` and that call would simply 403. */
  today: () => apiFetch<GateTripView[]>("/transport/gate/today"),
  /** Answers in `GateTripView`, not `TripView`: the
   * gate never receives a trip's items, quantities or the planners' note —
   * that is the whole reason `GateTripView` exists. */
  arrive: (id: string) => apiFetch<GateTripView>(`/transport/gate/${id}/arrive`, { method: "POST" }),
  depart: (id: string) => apiFetch<GateTripView>(`/transport/gate/${id}/depart`, { method: "POST" }),
  weigh: (id: string, body: RecordWeighingRequest) =>
    apiFetch<WeighingView>(`/transport/gate/${id}/weighings`, { method: "POST", body }),
  weighings: (tripId: string) => apiFetch<WeighingView[]>(`/transport/gate/${tripId}/weighings`),
};

// ---- allocation (inbound only) ---------------------------------------------

/** How a share of one weighbridge net was arrived at. `SPLIT_WEIGHING` is
 * deliberately absent: it would need intermediate readings the gate does not
 * record, so offering it would be a label with nothing behind it. */
export type AllocationBasis = "THIRD_PARTY_TICKET" | "DECLARED_PRORATA" | "MANUAL";

export type TripItemAllocationView = {
  itemId: string;
  position: number;
  supplierCompanyId: string | null;
  supplierName: string | null;
  declaredKg: number | null;
  /** What pro rata would give this item — a starting point, never a decision. */
  proposedKg: number | null;
  allocatedKg: number | null;
  allocationBasis: AllocationBasis | null;
  allocationTicketNo: string | null;
  allocationReason: string | null;
  allocatedBy: string | null;
  allocatedAt: string | null;
  /** A weighing landed after this was signed, so the net it was signed against is
   * no longer the trip's net. Settlement refuses such a delivery. */
  stale: boolean;
};

export type TripAllocationView = {
  tripId: string;
  tripNumber: string;
  /** Separate from a null netKg on purpose: the screen must say "not weighed yet"
   * rather than render a blank that reads as zero. */
  netKnown: boolean;
  netKg: number | null;
  grossKg: number | null;
  tareKg: number | null;
  declaredTotalKg: number | null;
  variancePct: number | null;
  massVarianceReason: string | null;
  items: TripItemAllocationView[];
};

export type AllocateTripRequest = {
  basis: AllocationBasis;
  items: { itemId: string; allocatedKg: number; ticketNo?: string; reason?: string }[];
  massVarianceReason?: string;
  varianceThresholdPct?: number;
};
